import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import useAuthStore from '../../store/authStore';
import DoctorDropdown from '../../components/DoctorDropdown';

const DEPARTMENTS = ['General', 'Cardiology', 'Dermatology', 'ENT', 'Gynecology', 'Neurology', 'Ophthalmology', 'Orthopedics', 'Pediatrics', 'Psychiatry', 'Radiology', 'Surgery', 'Urology'];
const REFERRAL_SOURCES = ['Walk-in', 'Referred from IPD', 'Referred from A&E', 'External Referral', 'Online Appointment', 'Phone Appointment'];
const CHIEF_COMPLAINTS = ['Fever', 'Cough', 'Headache', 'Chest Pain', 'Abdominal Pain', 'Back Pain', 'Shortness of Breath', 'Dizziness', 'Nausea/Vomiting', 'Joint Pain', 'Skin Rash', 'Eye Problem', 'Ear Problem', 'Urinary Complaint', 'Follow-up Visit', 'Other'];
const RELATIONS = ['S/O', 'D/O', 'W/O', 'B/O', 'F/O', 'M/O'];
const NATIONALITIES = ['Pakistani','Iranian','Afghan','Chinese','Emirati','Saudi','Qatari','Kuwaiti','Bahraini','Omani','Other'];
const OCCUPATIONS = ['Govt Employee','Private Employee','Unemployed','Student','Business','Retired','Farmer','Housewife','Other'];
const CITIES_PK = ['Karachi','Lahore','Islamabad','Rawalpindi','Faisalabad','Multan','Hyderabad','Peshawar','Quetta','Sialkot','Gujranwala','Sukkur','Other'].sort();
const BLOOD_GROUPS = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];

function Field({ label, required, children }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', backgroundColor: '#fff' };

const newPatientInit = {
  full_name: '', name_relation: 'S/O', father_name: '', gender: '', date_of_birth: '',
  age_years: '', age_months: '', cnic: '', phone: '', phone_alt: '',
  address: '', city: '', country: 'Pakistan', blood_group: '', religion: '',
  nationality: 'Pakistani', marital_status: '', occupation: '',
  allergies: '', chronic_conditions: '',
};

export default function OPDRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore(s => s.token);
  const headers = { Authorization: `Bearer ${token}` };
  const params = new URLSearchParams(location.search);

  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [showNewPatient, setShowNewPatient] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [panelCompanies, setPanelCompanies] = useState([]);
  const [consultFees, setConsultFees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newPatient, setNewPatient] = useState(newPatientInit);

  const [form, setForm] = useState({
    chief_complaint: '',
    doctor_id: '',
    doctor_name: '',
    department: 'General',
    referral_source: 'Walk-in',
    appointment_id: params.get('appointment_id') || '',
    payment_type: 'Cash',
    panel_company_id: '',
    panel_company_name: '',
    visit_fee: '',
    advance_paid: '',
    priority: 'Routine',
    notes: '',
  });

  useEffect(() => {
    axios.get('/api/appointments/panel-companies', { headers }).then(r => setPanelCompanies(r.data || [])).catch(() => {});
    axios.get('/api/appointments/fees', { headers }).then(r => setConsultFees(r.data || [])).catch(() => {});

    const pid = params.get('patient_id');
    const doctorName = params.get('doctor_name');
    const apptId = params.get('appointment_id');

    if (pid) {
      axios.get(`/api/patients/${pid}`, { headers }).then(r => {
        setPatient(r.data);
        setPatientSearch(`${r.data.full_name || r.data.name} (${r.data.mrn})`);
      }).catch(() => {});
    }
    if (doctorName) setForm(f => ({ ...f, doctor_name: decodeURIComponent(doctorName) }));
    if (apptId) {
      axios.get('/api/appointments', { headers, params: { id: apptId } }).then(r => {
        const a = (r.data || [])[0];
        if (a) { setAppointment(a); setForm(f => ({ ...f, doctor_id: a.doctor_id || '', doctor_name: a.doctor_name || '', appointment_id: apptId })); }
      }).catch(() => {});
    }
  }, []);

  const searchPatients = async (q) => {
    setPatientSearch(q);
    setPatient(null);
    if (!q || q.length < 2) { setPatientResults([]); return; }
    try {
      const res = await axios.get(`/api/patients?q=${q}`, { headers });
      setPatientResults(res.data?.patients || []);
    } catch { setPatientResults([]); }
  };

  const selectPatient = (p) => {
    setPatient(p);
    setPatientSearch(`${p.full_name || p.name} (${p.mrn})`);
    setPatientResults([]);
    setShowNewPatient(false);
  };

  const handleDoctorChange = (val) => {
    setForm(f => {
      const fee = consultFees.find(cf => cf.doctor_name === val);
      const feeAmt = fee ? (f.payment_type === 'Cash' ? fee.cash_fee : fee.panel_fee) : f.visit_fee;
      return { ...f, doctor_name: val, doctor_id: null, visit_fee: feeAmt || f.visit_fee };
    });
  };

  const fmtPhone = (v) => { const d = v.replace(/\D/g, '').slice(0, 11); if (d.length <= 4) return d; return d.slice(0, 4) + '-' + d.slice(4); };
  const fmtCNIC = (v) => { const d = v.replace(/\D/g, '').slice(0, 13); if (d.length <= 5) return d; if (d.length <= 12) return d.slice(0, 5) + '-' + d.slice(5); return d.slice(0, 5) + '-' + d.slice(5, 12) + '-' + d.slice(12); };

  const handleDOB = (dob) => {
    if (!dob) { setNewPatient(p => ({ ...p, date_of_birth: '', age_years: '', age_months: '' })); return; }
    const birth = new Date(dob);
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    if (today.getDate() < birth.getDate()) months--;
    if (months < 0) { years--; months += 12; }
    setNewPatient(p => ({ ...p, date_of_birth: dob, age_years: years >= 0 ? years : 0, age_months: months >= 0 ? months : 0 }));
  };

  const handleAgeYears = (val) => {
    if (!val) { setNewPatient(p => ({ ...p, age_years: '', age_months: '', date_of_birth: '' })); return; }
    const years = parseInt(val);
    if (isNaN(years)) return;
    setNewPatient(p => ({ ...p, age_years: val, age_months: '0', date_of_birth: `${new Date().getFullYear() - years}-01-01` }));
  };

  const setNP = (key, val) => setNewPatient(p => ({ ...p, [key]: val }));

  const registerNewPatient = async () => {
    if (!newPatient.full_name || !newPatient.gender || !newPatient.phone) {
      setError('Full Name, Gender and Phone are required for new patient.');
      return;
    }
    try {
      const payload = {
        full_name: newPatient.full_name,
        father_name: newPatient.father_name,
        name_relation: newPatient.name_relation,
        gender: newPatient.gender,
        date_of_birth: newPatient.date_of_birth,
        age_years: parseInt(newPatient.age_years) || 0,
        age_months: parseInt(newPatient.age_months) || 0,
        cnic: newPatient.cnic,
        phone: newPatient.phone,
        phone_alt: newPatient.phone_alt,
        blood_group: newPatient.blood_group,
        marital_status: newPatient.marital_status,
        religion: newPatient.religion,
        nationality: newPatient.nationality,
        occupation: newPatient.occupation,
        address: newPatient.address,
        city: newPatient.city,
        country: newPatient.country,
        allergies: newPatient.allergies,
        chronic_conditions: newPatient.chronic_conditions,
      };
      const res = await axios.post('/api/patients', payload, { headers });
      selectPatient(res.data.patient || res.data);
      setShowNewPatient(false);
      setNewPatient(newPatientInit);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to register patient.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patient) { setError('Please select a patient.'); return; }
    if (!form.chief_complaint) { setError('Please enter a chief complaint.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        patient_id: patient.id,
        visit_fee: parseFloat(form.visit_fee) || 0,
        advance_paid: parseFloat(form.advance_paid) || 0,
      };
      const res = await axios.post('/api/opd', payload, { headers });
      const visitId = res.data?.id || res.data?.visit?.id;
      printSlip(res.data?.visit || res.data, patient);
      navigate(`/opd/doctor-view?visit_id=${visitId}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create OPD visit.');
      setSaving(false);
    }
  };

  const printSlip = (visit, pt) => {
    const content = `
      <html><head><title>OPD Registration Slip</title>
      <style>body{font-family:Arial;padding:20px;font-size:13px}h2{margin:0 0 6px}hr{margin:8px 0}table{width:100%}td{padding:3px 0}td:first-child{font-weight:bold;width:130px}.token{font-size:28px;font-weight:bold;text-align:center;margin:12px 0;color:#2563eb}</style></head><body>
      <h2>OPD Registration Slip</h2><hr/>
      <div class="token">Token #${visit?.visit_number || visit?.queue_number || '-'}</div><hr/>
      <table>
        <tr><td>Patient:</td><td>${pt?.name || '-'} (${pt?.mrn || '-'})</td></tr>
        <tr><td>Doctor:</td><td>${form.doctor_name || '-'}</td></tr>
        <tr><td>Department:</td><td>${form.department}</td></tr>
        <tr><td>Complaint:</td><td>${form.chief_complaint}</td></tr>
        <tr><td>Payment:</td><td>${form.payment_type}</td></tr>
        <tr><td>Fee Paid:</td><td>PKR ${parseFloat(form.advance_paid) || 0}</td></tr>
        <tr><td>Date/Time:</td><td>${new Date().toLocaleString('en-PK')}</td></tr>
      </table>
      <hr/><p style="font-size:11px;text-align:center">Please show this slip at the doctor's counter</p>
      </body></html>`;
    const w = window.open('', '_blank', 'width=400,height=500');
    if (w) { w.document.open(); w.document.write(content); w.document.close(); w.print(); }
  };

  const sectionStyle = { backgroundColor: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, border: '1px solid #e5e7eb' };
  const sectionTitle = { fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 16 };

  return (
    <form onSubmit={handleSubmit} style={{ padding: 24, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>New OPD Registration</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: '4px 0 0' }}>Register patient for OPD consultation</p>
        </div>
        {patient && (
          <button type="submit" disabled={saving} style={{ backgroundColor: saving ? '#93c5fd' : '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Registering...' : 'Register & Issue Token'}
          </button>
        )}
      </div>

      {error && <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 14, color: '#991b1b', marginBottom: 16 }}>{error}</div>}

      {/* Patient Selection */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>Patient</div>

        {appointment && (
          <div style={{ padding: '10px 14px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1d4ed8', marginBottom: 16 }}>
            Linked to appointment on {appointment.appointment_date?.slice(0, 10)} at {appointment.appointment_time?.slice(0, 5)}
          </div>
        )}

        <div style={{ position: 'relative', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Search Existing Patient *</label>
            {!patient && !showNewPatient && (
              <span onClick={() => setShowNewPatient(true)} style={{ fontSize: 12, color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>
                + Register New Patient
              </span>
            )}
          </div>
          <input type="text" value={patientSearch}
            onChange={e => searchPatients(e.target.value)}
            placeholder="Search by name, MRN, CNIC, or phone..."
            style={inputStyle} />
          {patientResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: 250, overflowY: 'auto' }}>
              {patientResults.map(p => (
                <div key={p.id} onClick={() => selectPatient(p)}
                  style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{p.full_name || p.name}</div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>MRN: {p.mrn} | {p.gender} | Age: {p.age_years || p.age}y</div>
                    </div>
                    {p.allergies && <span style={{ fontSize: 11, padding: '2px 8px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10 }}>Allergies</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {patient && (
          <div style={{ padding: 16, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>{patient.full_name || patient.name}</div>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                  MRN: {patient.mrn} &nbsp;|&nbsp; {patient.gender} &nbsp;|&nbsp; Age: {patient.age_years || patient.age}y
                  {patient.blood_group && ` | ${patient.blood_group}`}
                </div>
                {patient.allergies && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {patient.allergies.split(/[,;]/).map((a, i) => a.trim() && (
                      <span key={i} style={{ padding: '2px 8px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10, fontSize: 12 }}>⚠ {a.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" onClick={() => { setPatient(null); setPatientSearch(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18 }}>×</button>
            </div>
          </div>
        )}

        {!patient && patientSearch.length >= 2 && patientResults.length === 0 && !showNewPatient && (
          <div style={{ marginTop: 8, padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
            No patient found for "<strong>{patientSearch}</strong>" —{' '}
            <span onClick={() => setShowNewPatient(true)} style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>register as new patient</span>
          </div>
        )}

        {!patient && (showNewPatient || (patientSearch.length >= 2 && patientResults.length === 0)) && (
          <div style={{ marginTop: 16, backgroundColor: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', backgroundColor: '#1e3a5f', color: '#fff', fontSize: 14, fontWeight: 700 }}>
              New Patient Registration
            </div>
            <div style={{ padding: 16 }}>
              {/* Personal Information */}
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid #e2e8f0' }}>Personal Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Field label="Full Name" required>
                  <input style={inputStyle} value={newPatient.full_name} onChange={e => setNP('full_name', e.target.value)} placeholder="Full name" />
                </Field>
                <Field label="Father / Guardian Name">
                  <div style={{ display: 'flex', gap: 6 }}>
                    <select style={{ ...inputStyle, width: 80, flexShrink: 0 }} value={newPatient.name_relation} onChange={e => setNP('name_relation', e.target.value)}>
                      {RELATIONS.map(r => <option key={r}>{r}</option>)}
                    </select>
                    <input style={{ ...inputStyle, flex: 1 }} value={newPatient.father_name} onChange={e => setNP('father_name', e.target.value)} placeholder="Guardian name" />
                  </div>
                </Field>
                <Field label="Gender" required>
                  <select style={inputStyle} value={newPatient.gender} onChange={e => setNP('gender', e.target.value)}>
                    <option value="">Select</option>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </Field>
                <Field label="Age (Years | Months | DOB)">
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="number" style={{ ...inputStyle, width: 70 }} value={newPatient.age_years} onChange={e => handleAgeYears(e.target.value)} placeholder="Yrs" min={0} max={150} />
                    <input type="number" style={{ ...inputStyle, width: 70 }} value={newPatient.age_months} onChange={e => setNP('age_months', e.target.value)} placeholder="Mo" min={0} max={11} />
                    <input type="date" style={{ ...inputStyle, flex: 1 }} value={newPatient.date_of_birth} onChange={e => handleDOB(e.target.value)} />
                  </div>
                </Field>
                <Field label="CNIC / B-Form">
                  <input style={inputStyle} value={newPatient.cnic} onChange={e => setNP('cnic', fmtCNIC(e.target.value))} placeholder="42301-1234567-8" />
                </Field>
                <Field label="Phone" required>
                  <input style={inputStyle} value={newPatient.phone} onChange={e => setNP('phone', fmtPhone(e.target.value))} placeholder="0300-1234567" />
                </Field>
                <Field label="Alternative Phone">
                  <input style={inputStyle} value={newPatient.phone_alt} onChange={e => setNP('phone_alt', fmtPhone(e.target.value))} placeholder="0300-1234567" />
                </Field>
                <Field label="Blood Group">
                  <select style={inputStyle} value={newPatient.blood_group} onChange={e => setNP('blood_group', e.target.value)}>
                    <option value="">Unknown</option>
                    {BLOOD_GROUPS.map(g => <option key={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Marital Status">
                  <select style={inputStyle} value={newPatient.marital_status} onChange={e => setNP('marital_status', e.target.value)}>
                    <option value="">Select</option>
                    <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
                  </select>
                </Field>
                <Field label="Religion">
                  <select style={inputStyle} value={newPatient.religion} onChange={e => setNP('religion', e.target.value)}>
                    <option value="">Select</option>
                    <option>Islam</option><option>Christianity</option><option>Hinduism</option><option>Other</option>
                  </select>
                </Field>
                <Field label="Nationality">
                  <select style={inputStyle} value={newPatient.nationality} onChange={e => setNP('nationality', e.target.value)}>
                    {NATIONALITIES.map(n => <option key={n}>{n}</option>)}
                  </select>
                </Field>
                <Field label="Occupation">
                  <select style={inputStyle} value={newPatient.occupation} onChange={e => setNP('occupation', e.target.value)}>
                    <option value="">Select</option>
                    {OCCUPATIONS.map(o => <option key={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Field label="Country">
                  <select style={inputStyle} value={newPatient.country} onChange={e => setNP('country', e.target.value)}>
                    <option>Pakistan</option><option>Iran</option><option>Afghanistan</option><option>UAE</option><option>Saudi Arabia</option><option>Other</option>
                  </select>
                </Field>
                <Field label="City">
                  <select style={inputStyle} value={newPatient.city} onChange={e => setNP('city', e.target.value)}>
                    <option value="">— Select City —</option>
                    {CITIES_PK.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Address">
                  <input style={inputStyle} value={newPatient.address} onChange={e => setNP('address', e.target.value)} placeholder="Street, area, city" />
                </Field>
              </div>
              {/* Medical */}
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 12, paddingBottom: 6, borderBottom: '2px solid #e2e8f0' }}>Medical History</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <Field label="Known Allergies">
                  <textarea style={{ ...inputStyle, height: 60 }} value={newPatient.allergies} onChange={e => setNP('allergies', e.target.value)} placeholder="e.g. Penicillin, Aspirin..." />
                </Field>
                <Field label="Chronic Conditions">
                  <textarea style={{ ...inputStyle, height: 60 }} value={newPatient.chronic_conditions} onChange={e => setNP('chronic_conditions', e.target.value)} placeholder="e.g. Diabetes, Hypertension..." />
                </Field>
              </div>
              <button type="button" onClick={registerNewPatient}
                style={{ padding: '9px 24px', border: 'none', borderRadius: 8, backgroundColor: '#059669', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Register & Select Patient
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Visit Details */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>Visit Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Chief Complaint" required>
              <select value={form.chief_complaint} onChange={e => setForm(f => ({ ...f, chief_complaint: e.target.value }))} style={inputStyle}>
                <option value="">Select complaint...</option>
                {CHIEF_COMPLAINTS.map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Doctor">
            <DoctorDropdown value={form.doctor_name} onChange={handleDoctorChange} label="" />
          </Field>
          <Field label="Department">
            <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} style={inputStyle}>
              {DEPARTMENTS.map(d => <option key={d}>{d}</option>)}
            </select>
          </Field>
          <Field label="Referral Source">
            <select value={form.referral_source} onChange={e => setForm(f => ({ ...f, referral_source: e.target.value }))} style={inputStyle}>
              {REFERRAL_SOURCES.map(r => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} style={inputStyle}>
              <option>Routine</option><option>Urgent</option>
            </select>
          </Field>
        </div>
      </div>

      {/* Payment */}
      <div style={sectionStyle}>
        <div style={sectionTitle}>Payment</div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 0 }}>
            {['Cash', 'Panel', 'Insurance'].map((pt, i) => (
              <button key={pt} type="button" onClick={() => setForm(f => ({ ...f, payment_type: pt }))}
                style={{
                  padding: '8px 20px', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: 14,
                  backgroundColor: form.payment_type === pt ? '#2563eb' : '#fff',
                  color: form.payment_type === pt ? '#fff' : '#374151',
                  borderRadius: i === 0 ? '6px 0 0 6px' : i === 2 ? '0 6px 6px 0' : 0,
                  borderRight: i !== 2 ? 'none' : '1px solid #d1d5db',
                }}>
                {pt}
              </button>
            ))}
          </div>
        </div>

        {(form.payment_type === 'Panel' || form.payment_type === 'Insurance') && (
          <div style={{ marginBottom: 16 }}>
            <Field label="Panel / Insurance Company">
              <select value={form.panel_company_id} onChange={e => {
                const c = panelCompanies.find(p => p.id === parseInt(e.target.value));
                setForm(f => ({ ...f, panel_company_id: e.target.value, panel_company_name: c?.name || '' }));
              }} style={inputStyle}>
                <option value="">Select company...</option>
                {panelCompanies.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
              </select>
            </Field>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Consultation Fee (PKR)">
            <input type="number" min="0" value={form.visit_fee} onChange={e => setForm(f => ({ ...f, visit_fee: e.target.value }))}
              placeholder="0.00" style={inputStyle} />
          </Field>
          <Field label="Advance Paid (PKR)">
            <input type="number" min="0" value={form.advance_paid} onChange={e => setForm(f => ({ ...f, advance_paid: e.target.value }))}
              placeholder="0.00" style={inputStyle} />
          </Field>
        </div>

        {form.visit_fee && (
          <div style={{ marginTop: 12, padding: '10px 14px', backgroundColor: '#f0fdf4', borderRadius: 8, fontSize: 13 }}>
            Balance Due: PKR <strong>{Math.max(0, parseFloat(form.visit_fee || 0) - parseFloat(form.advance_paid || 0)).toFixed(2)}</strong>
          </div>
        )}
      </div>
    </form>
  );
}
